import type { Node } from '@xyflow/react'
import { DEFAULT_INITIAL_WAIT_TIME } from '../constants'
import type { NodeDataBase } from '../types'
import { isAiAgentV2Type } from './ai-agent-v29'
import { eventsDictToArray } from './event-callbacks-utils'

/**
 * Replays the config normalizations the backend-load transform performs, which
 * the paste/import path skips (it feeds raw exported JSON straight onto the canvas):
 *
 * - Event callbacks can arrive as the keyed object (`{ hangup: {...} }`) instead
 *   of the array shape EventCallbacks renders; convert them or it crashes on `.map`.
 * - AI conversation `call` nodes from flows exported before the Call Start Behavior
 *   feature lack `initial_wait_time`. Seed the same default a freshly-created call
 *   node gets (`nodes.config.tsx`) so imports behave like newly-built flows instead
 *   of the backend's `0` ("agent speaks first") default. `== null` keeps a
 *   deliberate `0`; the runtime only honors the value on the first node of the call.
 *
 * Returns the patched config; callers layer node-specific fields (e.g. a
 * conflict-resolved `name`) on top.
 */
export const normalizeImportedNodeConfig = (
  node: Node<NodeDataBase<unknown>>,
): Record<string, unknown> => {
  const config = node.data.config as Record<string, unknown>
  const patch: Record<string, unknown> = {}

  const rawEvents = config.events
  if (rawEvents && !Array.isArray(rawEvents)) {
    patch.events = eventsDictToArray(rawEvents as Record<string, { url: string; method: string }>)
  }

  if (isAiAgentV2Type(node.type) && config.channel === 'call' && config.initial_wait_time == null) {
    patch.initial_wait_time = DEFAULT_INITIAL_WAIT_TIME
  }

  return { ...config, ...patch }
}
